import {useRef, useState} from 'react';
import {useScroll} from 'react-use';
import {
  Link,
  useCart,
  CartLineProvider,
  Money,
  fetchSync,
  AddToCartButton,
  Image,
  ProductOptionsProvider,
} from '@shopify/hydrogen';

import {Button, Text, CartLineItem, CartEmpty} from '~/components';

export function CartDetails({layout, onClose}) {
  const {lines} = useCart();
  const scrollRef = useRef(null);
  const {y} = useScroll(scrollRef);

  if (lines.length === 0) {
    return <CartEmpty onClose={onClose} layout={layout} />;
  }

  const container = {
    drawer: 'grid grid-cols-1 h-screen-no-nav grid-rows-[1fr_auto]',
    page: 'pb-12 grid md:grid-cols-2 md:items-start gap-8 md:gap-8 lg:gap-12',
  };

  const content = {
    drawer: 'px-6 pb-6 sm-max:pt-2 overflow-auto transition md:px-12',
    page: 'flex-grow md:translate-y-4',
  };

  const summary = {
    drawer: 'grid gap-6 p-6 border-t md:px-12',
    page: 'sticky top-nav grid gap-6 p-4 md:px-6 md:translate-y-4 bg-primary/5 rounded w-full',
  };

  const chosenTravelSet = lines.some((line) => {
    const type = line.merchandise.product.productType || '';
    const title = line.merchandise.product.title || '';
    return (
      type.toLowerCase().includes('travel set') ||
      title.toLowerCase().includes('travel set')
    );
  });

  const today = new Date();
  const promoStart = new Date('2024-11-01T00:00:00');
  const promoEnd = new Date('2024-11-30T23:59:59');
  const showPromo = today >= promoStart && today <= promoEnd;

  return (
    <form
      className={container[layout]}
      onSubmit={(evt) => evt.preventDefault()}
    >
      <section
        ref={scrollRef}
        aria-labelledby="cart-contents"
        className={`${content[layout]} ${
          y > 0 ? 'border-t' : 'pt-4'
        } overflow-y-auto`}
      >
        <ul className="grid gap-6 md:gap-10">
          {lines.map((line) => {
            return (
              <CartLineProvider key={line.id} line={line}>
                <CartLineItem />
              </CartLineProvider>
            );
          })}
        </ul>
      </section>
      {!chosenTravelSet && showPromo && <Promo lines={lines} />}

      <section aria-labelledby="summary-heading" className={summary[layout]}>
        <h2 id="summary-heading" className="sr-only">
          Order summary
        </h2>
        <OrderSummary />
        <CartCheckoutActions />
      </section>
    </form>
  );
}

function CartCheckoutActions() {
  const {checkoutUrl} = useCart();
  return (
    <>
      <div className="grid gap-4">
        {checkoutUrl ? (
          <Link to={checkoutUrl} prefetch={false} target="_self">
            <button className="btn lg-btn-solid-100">
              <span className="text-center">Continue to Checkout</span>
            </button>
          </Link>
        ) : null}
      </div>
    </>
  );
}

function OrderSummary() {
  const {cost} = useCart();
  return (
    <>
      <dl className="grid">
        <div className="flex items-center justify-between title title__md">
          <Text as="dt">Subtotal</Text>
          <Text as="dd">
            {cost?.subtotalAmount?.amount ? (
              <Money data={cost?.subtotalAmount} />
            ) : (
              '-'
            )}
          </Text>
        </div>
      </dl>
    </>
  );
}

function Promo({lines}) {
  const [giftAdded, setGiftAdded] = useState(false);
  const [selectedGiftId, setSelectedGiftId] = useState(null);
  const response = fetchSync('/api/promo');

  if (!response.ok) {
    console.error(
      `Unable to load top products ${response.url} returned a ${response.status}`,
    );
    return null;
  }

  const promo = response.json();
  if (promo.length === 0) {
    return null;
  }

  const qualifyingProducts = lines.some((line) => {
    const type = line.merchandise.product.productType || '';
    return type.includes('50ML') || type.includes('100ML');
  });

  const qualifyingQuantity = lines.reduce((acc, line) => {
    const type = line.merchandise.product.productType || '';
    return type.includes('50ML') || type.includes('100ML')
      ? acc + line.quantity
      : acc;
  }, 0);

  const giftOptions = [promo[5], promo[6]];

  if (qualifyingProducts && qualifyingQuantity === 1) {
    return (
      <section className="pt-4 pb-4 align-top mb-4 mt-4 border-t border-b">
        <div className="px-16">
          <p className="text-left text-sm pb-2">
            You are one fragrance away from a free gift!
          </p>
          <div className="relative w-full bg-white border border-black h-2.5 flex items-center">
            <div
              className="bg-black h-2 animate-pulse"
              style={{width: '75%'}}
            ></div>
            <div className="absolute right-0 -mr-4 flex items-center justify-center w-[1.75rem] h-[1.75rem] bg-black rounded-full">
              <span className="text-white text-center text-md font-bold">
                &#10003;
              </span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (qualifyingProducts && qualifyingQuantity >= 2) {
    return (
      <ProductOptionsProvider data={giftOptions}>
        <section className="py-8 align-top mb-4 mt-4 border-t border-b">
          <div className="px-16">
            <p className="text-left text-sm pb-2">
              Your order is eligible for a free gift!
            </p>
            <div className="relative w-full bg-white border border-black h-2.5 flex items-center">
              <div className="bg-black h-2" style={{width: '100%'}}></div>
              <div className="absolute right-0 -mr-4 flex items-center justify-center w-[1.75rem] h-[1.75rem] bg-black rounded-full">
                <span className="text-white text-center text-md font-bold">
                  &#10003;
                </span>
              </div>
            </div>
          </div>
        </section>
        <section className="px-16 align-top mb-4 mt-4">
          <p className="text-left text-xs pb-2">{promo[1].value}</p>
          <ul className="flex flex-col gap-4 pt-6">
            {giftOptions.map((gift, index) => (
              <li
                key={gift.key}
                className={`${
                  index !== 1 ? 'border-b' : ''
                } flex gap-4 pb-6 items-center`}
              >
                <div className="w-24 h-24 md:w-28 md:h-28 flex-shrink-0">
                  <Image
                    src={gift.reference.featuredImage.url}
                    alt={
                      gift.reference.featuredImage.altText ||
                      'Complimentary gift'
                    }
                    width={100}
                    height={100}
                    className="object-cover object-center w-full h-full border"
                  />
                </div>
                <div>
                  <p className="text-sm font-semibold">
                    {gift.reference.title}
                  </p>
                  <p className="text-xs">{gift.reference.description}</p>
                  <div className="mt-4">
                    <AddToCartButton
                      className="bg-black text-white uppercase text-xs font-semibold px-4 py-2"
                      variantId={gift.reference.variants.nodes[0].id}
                      quantity={1}
                      accessibleAddingToCartLabel="Adding item to your cart"
                      onClick={() => {
                        setSelectedGiftId(gift.key);
                        setGiftAdded(true);
                      }}
                      disabled={selectedGiftId !== null}
                    >
                      {selectedGiftId === gift.key
                        ? 'Added to Cart'
                        : 'Choose Gift'}
                    </AddToCartButton>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </ProductOptionsProvider>
    );
  }

  return null;
}
